import { useDataGridColumnsState } from '@ibsys/kit';
import type {
  DataGridColumnOrderState,
  DataGridColumnPinningState,
  DataGridColumnSizingState,
  DataGridColumnVisibilityState,
  DataGridFilterState,
  DataGridSortingState,
} from '@ibsys/kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CustomChartTableColumn, CustomChartTableView } from '@/entities/dashboards';
import { applyDataGridColumnsView, restoreColumnOrder, useDashboardPresets } from '@/entities/settings';
import { useColumnsPanelWidth } from '@/shared/hooks';
import { compareChartValues } from '../chartValues';
import type { IndexedTableRow } from './chartTable';
import { useChartTableView } from './useChartTableView';

type Params = {
  /** Диаграмма, чью таблицу настраивают */
  chartId: string;
  columns: CustomChartTableColumn[];
  rows: IndexedTableRow[];
  /** Вид колонок в составе дашборда; нет — все колонки выборки в её порядке */
  dashboardView: CustomChartTableView | undefined;
};

// Таблица диаграммы колонок не закрепляет
const NO_PINNING: DataGridColumnPinningState = {};

// Ширины колонок без вида — по умолчанию колонок
const NO_SIZING: DataGridColumnSizingState = {};

/**
 * Ширина панели колонок таблиц диаграмм, пока пользователь её не менял, и наименьшая ширина её
 * растягивания: шапка панели — название, «Очистить» и булавка закрепления — встаёт в одну строку.
 * Свою шапку кит переносит только по словам названия: в панели уже этой название уходит на вторую
 * строку, а ещё уже кнопки выходят за край панели и обрезаются. Замер шапки на стенде — 346 px
 * вместе с рамкой; величина взята с запасом под шрифт.
 */
const CHART_TABLE_COLUMNS_PANEL_WIDTH = 352;

/** Состав колонок грида: порядок, видимость и ширина — из них собирается вид колонок. */
type GridColumnsView = {
  order: DataGridColumnOrderState;
  visibility: DataGridColumnVisibilityState;
  sizing: DataGridColumnSizingState;
};

/**
 * Видимость колонок выборки по виду колонок. Колонка, о которой вид ничего не знает, не скрывалась:
 * выборка могла обзавестись новой колонкой уже после того, как вид был записан.
 */
const viewVisibility = (
  view: CustomChartTableView | undefined,
  columnCodes: string[],
): DataGridColumnVisibilityState => {
  const visible = view?.visibleColumns ?? [];
  const known = new Set([...visible, ...(view?.columnOrder ?? [])]);
  return Object.fromEntries(columnCodes.map((code) => [code, known.has(code) ? visible.includes(code) : true]));
};

/** Порядок колонок выборки по виду колонок: колонка, которой в виде нет, встаёт на своё место из выборки. */
const viewOrder = (view: CustomChartTableView | undefined, defaultOrder: string[]): DataGridColumnOrderState =>
  restoreColumnOrder(
    (view?.columnOrder ?? []).filter((code) => defaultOrder.includes(code)),
    defaultOrder,
  );

/** Границы диапазона фильтра числовой колонки и колонки-даты. */
type RangeFilterValue = { from?: number | string; to?: number | string };

const isRangeFilter = (value: unknown): value is RangeFilterValue =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Строка проходит текстовый фильтр: сравнение по подстроке без учёта регистра. */
const matchesText = (value: string | number | null, filter: string): boolean =>
  value !== null && String(value).toLowerCase().includes(filter.toLowerCase());

/**
 * Строка проходит фильтр диапазона. Границы сравниваются тем же способом, что и упорядочение той
 * же колонки: даты — моментами времени, числа — величиной, — иначе отбор по дате шёл бы по
 * написанию, а по числу — посимвольно.
 */
const matchesRange = (
  value: string | number | null,
  range: RangeFilterValue,
  type: CustomChartTableColumn['type'],
): boolean => {
  if (value === null || value === '') return false;
  if (range.from !== undefined && compareChartValues(value, range.from as string | number, type) < 0) return false;
  if (range.to !== undefined && compareChartValues(value, range.to as string | number, type) > 0) return false;
  return true;
};

/**
 * Вид таблицы диаграммы: состав, порядок и ширина колонок, упорядочение и фильтры.
 *
 * Состав, порядок и ширина колонок — вид колонок дашборда: его задаёт тот, кто дашбордом распоряжается,
 * а прочие меняют у себя поверх него ({@link useChartTableView}). Упорядочение и фильтры — личные у
 * каждого смотрящего и живут отдельно от состава дашборда, поэтому правка дашборда их не сбрасывает,
 * а кнопка сброса фильтров панели не затрагивает.
 *
 * Отбор и упорядочение идут по уже полученной выборке — новых обращений к бэкенду они не дают:
 * данные диаграммы целиком у клиента, и заново наполнять её, чтобы переставить те же строки,
 * незачем.
 */
export const useChartTableState = ({ chartId, columns, rows, dashboardView }: Params) => {
  const { chartTables, setChartTableSettings, chartTablesColumnsPanel, setChartTablesColumnsPanel } =
    useDashboardPresets();
  // Упорядочение и фильтры читаются один раз, на заходе: дальше ими владеет сам грид, и
  // перечитывание настроек после каждой записи возвращало бы таблицу к только что записанному.
  const savedRef = useRef(chartTables?.[chartId]);
  const saved = savedRef.current;

  const defaultOrder = useMemo<DataGridColumnOrderState>(() => columns.map((column) => column.code), [columns]);

  // Состав колонок грида на сейчас: смена одной его части записывается вместе с остальными
  const gridViewRef = useRef<GridColumnsView | null>(null);
  const applyGridViewRef = useRef<(view: CustomChartTableView | undefined) => void>(() => {
    // Грида ещё нет — выставлять вид некуда
  });

  const { view, changeView, clearView } = useChartTableView({
    chartId,
    dashboardView,
    onApplyView: (next) => applyGridViewRef.current(next),
  });

  // Начальный вид — на заходе; дальше составом колонок владеет грид
  const initialViewRef = useRef(view);
  const initialVisibility = useMemo(() => viewVisibility(initialViewRef.current, defaultOrder), [defaultOrder]);
  const initialOrder = useMemo(() => viewOrder(initialViewRef.current, defaultOrder), [defaultOrder]);
  const [columnSizing, setColumnSizing] = useState<DataGridColumnSizingState>(
    () => initialViewRef.current?.columnSizing ?? NO_SIZING,
  );

  const { gridProps } = useDataGridColumnsState({
    defaultOrder: initialOrder,
    defaultVisibility: initialVisibility,
  });
  const { onColumnOrderChange, onColumnVisibilityChange, onColumnPinningChange, columnOrder, columnVisibility } =
    gridProps;

  gridViewRef.current = {
    order: columnOrder ?? initialOrder,
    visibility: columnVisibility ?? initialVisibility,
    sizing: columnSizing,
  };

  applyGridViewRef.current = (next) => {
    applyDataGridColumnsView(
      { onColumnOrderChange, onColumnVisibilityChange, onColumnPinningChange },
      { order: viewOrder(next, defaultOrder), visibility: viewVisibility(next, defaultOrder), pinning: NO_PINNING },
    );
    setColumnSizing(next?.columnSizing ?? NO_SIZING);
  };

  /** Записывает вид колонок, сменённый пользователем в гриде, — целиком, со всеми его частями. */
  const commitGridView = useCallback(
    (change: Partial<GridColumnsView>) => {
      const current = gridViewRef.current;
      if (!current) return;
      const next = { ...current, ...change };
      changeView({
        visibleColumns: next.order.filter((code) => next.visibility[code] !== false),
        columnOrder: next.order,
        columnSizing: next.sizing,
      });
    },
    [changeView],
  );

  const handleColumnOrderChange = useCallback(
    (order: DataGridColumnOrderState) => {
      onColumnOrderChange(order);
      commitGridView({ order });
    },
    [onColumnOrderChange, commitGridView],
  );

  const handleColumnVisibilityChange = useCallback(
    (visibility: DataGridColumnVisibilityState) => {
      onColumnVisibilityChange(visibility);
      commitGridView({ visibility });
    },
    [onColumnVisibilityChange, commitGridView],
  );

  const handleColumnSizingChange = useCallback(
    (sizing: DataGridColumnSizingState) => {
      setColumnSizing(sizing);
      commitGridView({ sizing });
    },
    [commitGridView],
  );

  const [sorting, setSorting] = useState<DataGridSortingState>(saved?.sort ?? []);
  const [filters, setFilters] = useState<DataGridFilterState>(saved?.filters ?? {});

  const persist = setChartTableSettings;

  // Состав развёрнутых колонок меняется с данными, а порядок колонок грид держит свой, начатый на
  // заходе: колонка, которой в нём нет, встала бы в конец таблицы. Ставим её на место из ответа —
  // следом за соседкой слева, и колонка нового месяца встаёт среди месяцев, а не за итоговыми;
  // видимой она остаётся сама — скрытой грид считает только колонку, скрытую явно. Это не правка
  // пользователя, поэтому вид колонок от неё не записывается.
  useEffect(() => {
    if (defaultOrder.every((code) => columnOrder.includes(code))) return;
    onColumnOrderChange(restoreColumnOrder(columnOrder, defaultOrder));
  }, [defaultOrder, columnOrder, onColumnOrderChange]);

  const handleSortingChange = useCallback(
    (next: DataGridSortingState) => {
      setSorting(next);
      persist(chartId, { sort: next });
    },
    [chartId, persist],
  );

  const handleFiltersChange = useCallback(
    (next: DataGridFilterState) => {
      setFilters(next);
      persist(chartId, { filters: next });
    },
    [chartId, persist],
  );

  // Ширина и закрепление панели колонок одни на все таблицы диаграмм, а не подбираются китом под
  // названия колонок: иначе панель открывалась бы у каждой таблицы своим размером.
  const persistColumnsPanelWidth = useCallback(
    (width: number) => setChartTablesColumnsPanel({ width }),
    [setChartTablesColumnsPanel],
  );
  const { width: columnsPanelWidth, onWidthChange: onColumnsPanelWidthChange } = useColumnsPanelWidth(
    chartTablesColumnsPanel?.width,
    persistColumnsPanelWidth,
  );
  const handleSidePanelPinnedChange = useCallback(
    (pinned: boolean) => setChartTablesColumnsPanel({ pinned }),
    [setChartTablesColumnsPanel],
  );

  const columnByCode = useMemo(() => new Map(columns.map((column) => [column.code, column])), [columns]);

  const visibleRows = useMemo(() => {
    const conditions = Object.entries(filters).filter(([, value]) => value !== undefined && value !== null);
    const filtered =
      conditions.length === 0
        ? rows
        : rows.filter((row) =>
            conditions.every(([code, condition]) => {
              const column = columnByCode.get(code);
              // Фильтр по колонке, которой в выборке уже нет, строк не отсеивает: он остался от
              // прежнего наполнения и снимется, как только пользователь его тронет.
              if (!column) return true;
              const value = row.values[code] ?? null;
              if (typeof condition === 'string') return condition === '' || matchesText(value, condition);
              if (isRangeFilter(condition)) return matchesRange(value, condition, column.type);
              return true;
            }),
          );

    const [order] = sorting;
    const column = order && columnByCode.get(order.columnId);
    if (!order || !column) return filtered;
    const direction = order.direction === 'desc' ? -1 : 1;
    return [...filtered].sort(
      (first, second) =>
        direction *
        compareChartValues(first.values[column.code] ?? null, second.values[column.code] ?? null, column.type),
    );
  }, [rows, filters, sorting, columnByCode]);

  return {
    /** Пропы состава, порядка и ширины колонок для грида */
    gridProps: {
      ...gridProps,
      onColumnOrderChange: handleColumnOrderChange,
      onColumnVisibilityChange: handleColumnVisibilityChange,
      columnSizing,
      onColumnSizingChange: handleColumnSizingChange,
    },
    /** Панель колонок грида: общая ширина таблиц диаграмм и «Очистить» */
    columnsPanel: {
      // Записанная прежде ширина могла оказаться уже наименьшей — панель всё равно не уже её
      width: Math.max(columnsPanelWidth ?? CHART_TABLE_COLUMNS_PANEL_WIDTH, CHART_TABLE_COLUMNS_PANEL_WIDTH),
      // Задана у этих таблиц, а не в настройках кита: прочие таблицы подбирают панель под колонки сами
      minWidth: CHART_TABLE_COLUMNS_PANEL_WIDTH,
      onWidthChange: onColumnsPanelWidthChange,
      onReset: clearView,
    },
    /** Закрепление панели колонок — общее для таблиц диаграмм */
    sidePanelPinned: chartTablesColumnsPanel?.pinned ?? false,
    onSidePanelPinnedChange: handleSidePanelPinnedChange,
    rows: visibleRows,
    sorting,
    onSortingChange: handleSortingChange,
    filters,
    onFiltersChange: handleFiltersChange,
  };
};
